import * as React from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactFileReader from "react-file-reader";
import { notification } from "antd";
import { getRequest, putRequest, delRequest } from "../../core/network";
import { uploadFile } from "../../core/aws";
import endpoints from "../../constants/endPoints.json";
import { getEvent } from "../../app/store/actions/eventActions";
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformButton,
  PlatformLoadingBlock,
} from "../../app/components/platform";
import HeroTemplate from "./templates/HeroTemplate";
import SplitTemplate from "./templates/SplitTemplate";
import MinimalTemplate from "./templates/MinimalTemplate";

const TEMPLATE_OPTIONS = [
  { id: "hero", label: "Hero", description: "Full-width hero with form below" },
  { id: "split", label: "Split", description: "Brand panel + form side by side" },
  { id: "minimal", label: "Minimal", description: "Compact centered card" },
];

const TEMPLATE_COMPONENTS = {
  hero: HeroTemplate,
  split: SplitTemplate,
  minimal: MinimalTemplate,
};

function registrationEndpoints(role) {
  if (role === "Super") return endpoints.super.registrationPages;
  if (role === "Partner") return endpoints.partner.registrationPages;
  return endpoints.admin.registrationPages;
}

const defaultBranding = {
  headline: "",
  subtitle: "",
  primaryColor: "#6B21A8",
  accentColor: "#1f2937",
  logoUrl: "",
  ctaText: "Register",
  footerText: "",
};

export function RegistrationPageEditor({ token, role, event, getEvent, isLoading }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const ep = registrationEndpoints(role);

  const [loadingPage, setLoadingPage] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [logoFile, setLogoFile] = React.useState(null);
  const [hasPage, setHasPage] = React.useState(false);
  const [form, setForm] = React.useState({
    template: "hero",
    slug: "",
    enabled: true,
    branding: { ...defaultBranding },
  });

  React.useEffect(() => {
    getEvent({ eventId });
  }, [eventId, getEvent]);

  React.useEffect(() => {
    setLoadingPage(true);
    getRequest({ eventId }, null, token, ep.get)
      .then((res) => {
        const page = res.data.page;
        if (page) {
          setHasPage(true);
          setForm({
            template: page.template,
            slug: page.slug,
            enabled: page.enabled,
            branding: { ...defaultBranding, ...page.branding },
          });
        } else {
          setHasPage(false);
          setForm((f) => ({
            ...f,
            branding: { ...f.branding, headline: event?.title || "" },
          }));
        }
      })
      .catch((err) => {
        notification.error({
          message: "Failed to load registration page",
          description: err?.response?.data?.message,
        });
      })
      .finally(() => setLoadingPage(false));
  }, [eventId, token, ep.get, event?.title]);

  const updateBranding = (key, value) => {
    setForm((f) => ({ ...f, branding: { ...f.branding, [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let logoUrl = form.branding.logoUrl;
      if (logoFile) {
        logoUrl = await uploadFile(logoFile);
      }
      const payload = {
        eventId,
        template: form.template,
        enabled: form.enabled,
        slug: form.slug || undefined,
        branding: { ...form.branding, logoUrl },
      };
      await putRequest(payload, null, null, token, ep.upsert);
      notification.success({ message: hasPage ? "Registration page updated" : "Registration page created" });
      setHasPage(true);
      setLogoFile(null);
      const refreshed = await getRequest({ eventId }, null, token, ep.get);
      if (refreshed.data.page?.slug) {
        setForm((f) => ({ ...f, slug: refreshed.data.page.slug }));
      }
    } catch (err) {
      notification.error({
        message: "Save failed",
        description: err?.response?.data?.message || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this event's registration page?")) return;
    try {
      await delRequest({ eventId }, null, token, ep.delete);
      notification.success({ message: "Registration page removed" });
      navigate(`/event/dashboard/${eventId}`);
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || "Delete failed" });
    }
  };

  const copyLink = () => {
    if (!form.slug) return;
    navigator.clipboard.writeText(`${window.location.origin}/register/${form.slug}`);
    notification.success({ message: "Link copied" });
  };

  const PreviewTemplate = TEMPLATE_COMPONENTS[form.template] || HeroTemplate;
  const previewPage = { branding: form.branding, template: form.template };
  const noop = () => {};

  if ((isLoading && !event) || loadingPage) {
    return (
      <PlatformPage>
        <PlatformLoadingBlock />
      </PlatformPage>
    );
  }

  return (
    <PlatformPage>
      <PlatformPageHeader
        title="Registration page"
        subtitle={event?.title}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <PlatformButton variant="ghost" onClick={() => navigate(`/event/dashboard/${eventId}`)}>
              Back to Event
            </PlatformButton>
            {hasPage && form.slug && (
              <>
                <Tooltip title="Copy shareable link">
                  <IconButton onClick={copyLink} size="small">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <PlatformButton
                  variant="ghost"
                  component="a"
                  href={`/register/${form.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  Preview
                </PlatformButton>
              </>
            )}
            {hasPage && (
              <PlatformButton variant="danger" startIcon={<DeleteIcon />} onClick={handleDelete}>
                Remove
              </PlatformButton>
            )}
            <PlatformButton onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : hasPage ? "Save changes" : "Create page"}
            </PlatformButton>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            <PlatformCard title="Template">
              <Stack spacing={1}>
                {TEMPLATE_OPTIONS.map((opt) => (
                  <Box
                    key={opt.id}
                    onClick={() => setForm((f) => ({ ...f, template: opt.id }))}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: form.template === opt.id ? "primary.main" : "divider",
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.light" },
                    }}
                  >
                    <Typography fontWeight={700}>{opt.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {opt.description}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </PlatformCard>

            <PlatformCard title="Branding">
              <Stack spacing={2}>
                <TextField
                  label="Headline"
                  fullWidth
                  value={form.branding.headline}
                  onChange={(e) => updateBranding("headline", e.target.value)}
                />
                <TextField
                  label="Subtitle"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.branding.subtitle}
                  onChange={(e) => updateBranding("subtitle", e.target.value)}
                />
                <TextField
                  label="CTA button text"
                  fullWidth
                  value={form.branding.ctaText}
                  onChange={(e) => updateBranding("ctaText", e.target.value)}
                />
                <TextField
                  label="Footer text"
                  fullWidth
                  value={form.branding.footerText}
                  onChange={(e) => updateBranding("footerText", e.target.value)}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Primary color"
                    type="color"
                    value={form.branding.primaryColor}
                    onChange={(e) => updateBranding("primaryColor", e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="Accent color (text & inputs)"
                    type="color"
                    value={form.branding.accentColor}
                    onChange={(e) => updateBranding("accentColor", e.target.value)}
                    sx={{ width: 120 }}
                  />
                </Stack>
                <Stack spacing={1}>
                  <ReactFileReader
                    multipleFiles={false}
                    handleFiles={(files) => setLogoFile(files[0])}
                    fileTypes={[".jpeg", ".jpg", ".png", ".svg", ".webp"]}
                  >
                    <Button variant="outlined" startIcon={<UploadIcon />} fullWidth>
                      Upload logo
                    </Button>
                  </ReactFileReader>
                  {logoFile && <Chip label={logoFile.name} onDelete={() => setLogoFile(null)} />}
                  {form.branding.logoUrl && !logoFile && (
                    <Box component="img" src={form.branding.logoUrl} alt="Logo" sx={{ maxHeight: 48 }} />
                  )}
                </Stack>
              </Stack>
            </PlatformCard>

            <PlatformCard title="Settings">
              <Stack spacing={2}>
                <TextField
                  label="URL slug"
                  fullWidth
                  helperText={
                    form.slug
                      ? `Shareable link: /register/${form.slug}`
                      : "Auto-generated from event title on save"
                  }
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder="Leave blank to auto-generate"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.enabled}
                      onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    />
                  }
                  label="Page enabled"
                />
                <Typography variant="body2" color="text.secondary">
                  Auto-approve is configured on the event (Edit event → Registration → Auto-approve registrations).
                  {event?.autoAccept
                    ? " Currently enabled for this event."
                    : " Currently disabled — requests require manual review."}
                </Typography>
              </Stack>
            </PlatformCard>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={7}>
          <PlatformCard title="Preview">
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                maxHeight: 720,
                overflowY: "auto",
                pointerEvents: "none",
              }}
            >
              {event && (
                <PreviewTemplate
                  page={previewPage}
                  event={event}
                  user={null}
                  onSubmit={noop}
                  submitting={false}
                />
              )}
            </Box>
          </PlatformCard>
        </Grid>
      </Grid>
    </PlatformPage>
  );
}

const mapStateToProps = (state) => ({
  token: state?.auth?.token,
  role: state?.auth?.token?.split(" ")[0],
  event: state?.events?.results,
  isLoading: state?.events?.isLoading,
});

export default connect(mapStateToProps, { getEvent })(RegistrationPageEditor);
